import React from 'react';
import { X, Minus, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../atoms/button';

export interface SyncErrorInfo {
  message: string;
  requiresReauth: boolean;
}

export interface SyncProgressToastProps {
  isVisible: boolean;
  isMinimized: boolean;
  onMinimize: () => void;
  onExpand?: () => void;
  onClose: () => void;
  currentFile: number;
  totalFiles: number;
  currentFileName?: string;
  isComplete: boolean;
  error?: SyncErrorInfo | string | null;
  onReauthenticate?: () => void;
}

// Google Drive Icon SVG
const GoogleDriveIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
  </svg>
);

export function SyncProgressToast({
  isVisible,
  isMinimized,
  onMinimize,
  onExpand,
  onClose,
  currentFile,
  totalFiles,
  currentFileName,
  isComplete,
  error,
  onReauthenticate,
}: SyncProgressToastProps) {
  if (!isVisible) return null;

  const progressPercent = totalFiles > 0 ? (currentFile / totalFiles) * 100 : 0;
  
  // Normalize error to object format
  const errorInfo: SyncErrorInfo | null = error
    ? typeof error === 'string'
      ? { message: error, requiresReauth: false }
      : error
    : null;

  // Minimized state
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-3 cursor-pointer hover:shadow-xl transition-shadow"
        onClick={onExpand || onMinimize}
      >
        <div className="flex items-center gap-2">
          {isComplete ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : errorInfo ? (
            <AlertCircle className="w-4 h-4 text-red-500" />
          ) : (
            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}
          <span className="text-sm text-gray-600">
            {isComplete ? 'Done' : errorInfo ? 'Error' : `${currentFile}/${totalFiles}`}
          </span>
        </div>
      </div>
    );
  }

  // Full expanded state
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <span className="font-medium text-gray-900">
          {isComplete ? '' : errorInfo ? 'Upload failed' : 'Uploading files'}
        </span>
        <div className="flex items-center gap-1">
          {!isComplete && !errorInfo && (
            <button
              onClick={onMinimize}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label="Minimize"
            >
              <Minus className="w-4 h-4 text-gray-500" />
            </button>
          )}
          {(isComplete || errorInfo) && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Syncing State */}
        {!isComplete && !errorInfo && (
          <div className="space-y-3">
            <div className="flex justify-between items-start gap-4">
              <p className="text-sm text-gray-600">
                Your files are now adding to your project context
              </p>
              <span className="text-sm text-gray-600 whitespace-nowrap font-medium">
                {currentFile} of {totalFiles}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            {currentFileName && (
              <p className="text-xs text-gray-500 truncate">
                {currentFileName}
              </p>
            )}
          </div>
        )}

        {/* Complete State */}
        {isComplete && !errorInfo && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
              <h3 className="font-medium text-gray-900">
                All files uploaded successfully!
              </h3>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        )}

        {/* Error State */}
        {errorInfo && (
          <>
            <div className="flex items-center justify-center mb-3">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h3 className="text-center font-medium text-gray-900 mb-2">
              Upload failed
            </h3>
            <p className="text-center text-sm text-gray-600 mb-4">
              {errorInfo.message}
            </p>
            
            {errorInfo.requiresReauth && onReauthenticate && (
              <Button
                variant="primary"
                className="w-full mb-2"
                onClick={onReauthenticate}
              >
                Re-authenticate
              </Button>
            )}
            
            <Button
              variant="outline"
              className="w-full"
              onClick={onClose}
            >
              Close
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default SyncProgressToast;
